import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '@material-ui/core/Button';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Alert from '@material-ui/lab/Alert';
import Chart from 'chart.js';
import api from 'api/api';
import { DatePickerRangeModal } from 'components/modals';
import { ghsFormatter } from 'utils/formatters';
import { IApiResponse } from 'utils/models';
import Loader from 'ui/Loader/Loader';
import accountsStore from 'store/accountsStore';
import AccountSelector from './AccountSelector';
import { IExpenditure, IStatisticsData, TxnType } from './statistics.model';
import { bindChartEvents, chartConfig } from './utils';
import styles from './Statistics.module.css';
import calendar from 'assets/img/calendar.png';

const txnTypeNamesMap: Record<TxnType, string> = {
  FDT: 'Funds Transfers',
  MMT: 'Mobile Money Transfers',
  BLP: 'Bill Payments',
  PCL: 'Prepaid Card Load',
  OET: 'Others',
};

const defaultLabels = Object.values(txnTypeNamesMap);
// const defaultAmounts = new Array(defaultLabels.length).fill(0) as number[];

const ghs = (num: number) => ghsFormatter.format(num);

const Statistics = () => {
  const [isDatePickerModalOpen, setDatePickerModalOpen] = useState<boolean>(false);
  const [selectedDateRange, handleDateRangeChange] = useState([
    new Date().setDate(new Date().getDate() - 30),
    new Date().setDate(new Date().getDate() + 60),
  ]);
  const [selectedAccount, setSelectedAccount] = useState<string | null>(null);
  const [fetching, setFetching] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  const { accounts } = accountsStore;
  const selectedAccountCurrency = accounts.length
    ? accounts.find((acc) => acc.accountNumber === selectedAccount)?.currency
    : 'GHS';

  const [startRangeDate, endRangeDate] = selectedDateRange;

  const [chartData, setChartData] = useState<{ dataItems?: number[]; labels: string[]; total: number }>({
    dataItems: [],
    labels: [],
    total: 0,
  });

  const getParsedDate: (date) => string = useCallback(
    // eslint-disable-next-line newline-per-chained-call
    (date) => new Date(date).toJSON()?.slice(0, 10)?.split('-').reverse().join('-') || '',
    [],
  );

  const theme = useTheme();
  const xlMore = useMediaQuery(theme.breakpoints.up('xl'));

  const renderChart = useCallback(
    (dataItems?: number[], labels?: string[]) => {
      setTimeout(() => {
        const chartEl: HTMLCanvasElement | null = document.getElementById('chart') as HTMLCanvasElement | null;
        const chartLegendEL: HTMLElement | null = document.getElementById('chartLegend');

        const ctx = chartEl?.getContext('2d');
        if (ctx) {
          const myChart = new Chart(ctx, chartConfig({ dataItems, labels, xlMore }));

          if (chartLegendEL) {
            chartLegendEL.innerHTML = myChart.generateLegend() as string;
          }
          bindChartEvents(myChart, document);
        }
      }, 100);
    },
    [xlMore],
  );

  const fetchStatistics = useCallback(() => {
    setErrMsg('');
    const defaultData = { labels: defaultLabels, total: 0 };
    if (!selectedAccount) {
      setChartData(defaultData);
      return;
    }

    setFetching(true);
    const requestBody = {
      accountNumber: selectedAccount,
      startDate: getParsedDate(startRangeDate),
      endDate: getParsedDate(endRangeDate),
    };
    api
      .post('/account/transactions/getCustomerExpenditure', requestBody)
      .then((data: IStatisticsData) => {
        // console.log('expend', data, requestBody);
        if (data.dashboardExpenditureList && data.dashboardExpenditureList.length) {
          const labs: string[] = [];
          const vals: number[] = [];

          data.dashboardExpenditureList.forEach((exp: IExpenditure, i) => {
            labs[i] = txnTypeNamesMap[exp.txnType];
            vals[i] = exp.totalAmount;
          });
          setChartData({ dataItems: vals, labels: labs, total: data.totalAmount ?? 0 });
        } else {
          setChartData(defaultData);
        }
        if (!data.totalAmount) {
          setChartData(defaultData);
        }
      })
      .catch((error: IApiResponse) => {
        setErrMsg(error.description);

        setChartData(defaultData);
      })
      .finally(() => {
        setFetching(false);
      });
  }, [startRangeDate, endRangeDate, getParsedDate, selectedAccount]);

  useEffect(() => {
    renderChart(chartData.dataItems, chartData.labels);
  }, [chartData, renderChart]);

  useEffect(() => fetchStatistics(), [fetchStatistics]);

  const openDatePickerModal: () => unknown = () => setDatePickerModalOpen(true);
  const closeDatePickerModal: () => unknown = () => setDatePickerModalOpen(false);

  const getDateRange: (startDate, endDate) => void = (startDate, endDate) => {
    handleDateRangeChange([startDate, endDate]);
  };

  const errorAlert = useMemo(() => {
    if (!fetching && errMsg) {
      const button = (
        <Button color="inherit" size="small" onClick={fetchStatistics}>
          RETRY
        </Button>
      );

      return (
        <Alert severity="error" action={button}>
          {errMsg}
        </Alert>
      );
    }
    return null;
  }, [errMsg, fetching, fetchStatistics]);

  const tAmm = ghs(chartData.total).split(/\s/, 2)[1];

  return (
    <div className={styles.content}>
      <div className={styles.title}>Customer Expenditure Analysis</div>
      {errorAlert}
      {fetching ? (
        <Loader />
      ) : (
        <>
          <div className={styles.chartWrapper}>
            <div className={styles.chartContainer}>
              <canvas aria-label="ChartJs" id="chart" />
              <div className={styles.totalSpent}>
                <span className={styles.totalValue}>{tAmm}</span>
                <span className={styles.totalCurrency}>{selectedAccountCurrency}</span>
                <span className={styles.totalDescription}>Total spent</span>
              </div>
            </div>
            <div id="chartLegend" className={styles.chartLegend} />
          </div>
        </>
      )}
      <div className={styles.controls}>
        <div className={styles.accountSelectorWrapper}>
          <AccountSelector onAccountChanged={(acc) => setSelectedAccount(acc)} />
        </div>
        <div
          className={styles.datePickerInput}
          onClick={openDatePickerModal}
          onKeyPress={openDatePickerModal}
          role="button"
          tabIndex={0}
        >
          <span className={styles.datePickerInputValue}>
            {`${getParsedDate(selectedDateRange[0])} - ${getParsedDate(selectedDateRange[1])}`}
          </span>
          <img className={styles.datePickerInputIcon} src={calendar} alt="Calendar logo" />
        </div>
      </div>
      <DatePickerRangeModal
        open={isDatePickerModalOpen}
        startRangeDate={selectedDateRange[0]}
        endRangeDate={selectedDateRange[1]}
        onClose={closeDatePickerModal}
        getDateRange={getDateRange}
      />
    </div>
  );
};

export default Statistics;
